// FeesEntryView.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Stack,
  Avatar,
  useTheme,
  alpha,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  // FilterList as FilterIcon,
  // Print as PrintIcon,
  // Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Warning as WarningIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon,
  // Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import AuthService from '../Auth/AuthService';

const FeesManagementPage = () => {
  const theme = useTheme();
  const [feesData, setFeesData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [selectedFees, setSelectedFees] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchFeesData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AuthService.getAuth(AuthService.pathUrl("GET", `/fees`));
      
      let data = response;
      if (response && response.data) {
        data = response;
      }
      
      const feesArray = Array.isArray(data) ? data : [];
      
      // Transform API data to match component structure if needed
      const transformedData = feesArray.map(item => ({
        feesEntryId: item.feesEntryId || item.id,
        classRoomId: item.classRoomId || item.classroomId,
        studentId: item.studentId,
        studentName: item.studentName || item.student?.name || 'Unknown',
        annualFees: item.annualFees || item.totalFees || 0,
        discountAmount: item.discountAmount || 0,
        paidAmount: item.paidAmount || 0,
        dueDate: item.dueDate,
        paymentMode: item.paymentMode || 'Cash',
        status: item.status || 'Pending',
        paymentDate: item.paymentDate || null,
        vehicleFees: item.vehicleFees || 0,
        standard: item.standard || item.classroom?.standard || 'N/A',
        fees: item.fees || [],
        createdAt: item.createdAt,
      }));
      
      setFeesData(transformedData);
      setFilteredData(transformedData);
    } catch (err) {
      console.error('Error fetching fees data:', err);
      setError('Failed to load fees data. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeesData();
  }, []);

  useEffect(() => {
    let filtered = feesData;

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.standard?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.studentId?.toString().includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }

    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((item) => {
        if (!item.dueDate) return false;
        const dueDate = new Date(item.dueDate);
        return dueDate >= dateRange.start && dueDate <= dateRange.end;
      });
    }

    setFilteredData(filtered);
    setPage(0);
  }, [searchTerm, statusFilter, dateRange, feesData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      Paid: {
        color: 'success',
        icon: <CheckCircleIcon />,
        bgColor: alpha(theme.palette.success.main, 0.1),
      },
      Pending: {
        color: 'warning',
        icon: <PendingIcon />,
        bgColor: alpha(theme.palette.warning.main, 0.1),
      },
      Partial: {
        color: 'info',
        icon: <WarningIcon />,
        bgColor: alpha(theme.palette.info.main, 0.1),
      },
    };

    const config = statusConfig[status] || statusConfig.Pending;
    return (
      <Chip
        icon={config.icon}
        label={status}
        color={config.color}
        size="small"
        sx={{
          fontWeight: 600,
          backgroundColor: config.bgColor,
          '& .MuiChip-icon': {
            color: `${config.color}.main`,
          },
        }}
      />
    );
  };

  const getPaymentModeChip = (mode) => {
    const modeConfig = {
      online: { color: 'primary', label: 'Online' },
      cash: { color: 'secondary', label: 'Cash' },
    };
    const config = modeConfig[mode?.toLowerCase()] || modeConfig.cash;
    return (
      <Chip
        label={config.label}
        size="small"
        variant="outlined"
        color={config.color}
        sx={{ fontWeight: 500 }}
      />
    );
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Calculate total
  // const calculateTotal = (fees) => {
  //   if (!fees || !Array.isArray(fees)) return 0;
  //   return fees.reduce((sum, fee) => sum + (fee.amount || 0), 0);
  // };

  const handleViewDetails = (fees) => {
    setSelectedFees(fees);
    setViewDialogOpen(true);
  };

  // Delete fees entry
  const handleDelete = async (feesId) => {
    setLoading(true);
    try {
      await AuthService.deleteAuth(AuthService.pathUrl("DELETE",`/fees/${feesId}`));
      const updatedData = feesData.filter((item) => item.feesEntryId !== feesId);
      setFeesData(updatedData);
      setFilteredData(updatedData);
      setSuccessMessage('Fee entry deleted successfully');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting fee entry:', err);
      setError('Failed to delete fee entry. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleRefresh = () => {
    fetchFeesData();
  };

  const statsData = {
    totalStudents: feesData.length,
    totalCollection: feesData.reduce((sum, item) => sum + (item.paidAmount || 0), 0),
    pendingAmount: feesData.reduce(
      (sum, item) => sum + (item.status === 'Pending' ? (item.annualFees || 0) - (item.paidAmount || 0) : 0),
      0
    ),
    paidCount: feesData.filter((item) => item.status === 'Paid').length,
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setError(null);
    setSuccessMessage(null);
  };

  return (
      <Box sx={{ p: 3, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Fees Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all fee entries
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Students
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {statsData.totalStudents}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
                    <SchoolIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Total Collection
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="success.main">
                      {formatCurrency(statsData.totalCollection)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main' }}>
                    <CurrencyRupeeIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Pending Amount
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="warning.main">
                      {formatCurrency(statsData.pendingAmount)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main' }}>
                    <PendingIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px 0 rgba(0,0,0,0.05)' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Paid Students
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="info.main">
                      {statsData.paidCount}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main' }}>
                    <CheckCircleIcon />
                  </Avatar>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by student name, ID, or standard"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="Paid">Paid</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Partial">Partial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="From Date"
                value={dateRange.start ? dayjs(dateRange.start) : null}
                onChange={(newValue) =>
                  setDateRange({ ...dateRange, start: newValue ? newValue.toDate() : null })
                }
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="To Date"
                value={dateRange.end ? dayjs(dateRange.end) : null}
                onChange={(newValue) =>
                  setDateRange({ ...dateRange, end: newValue ? newValue.toDate() : null })
                }
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {loading && <LinearProgress />}
          {!loading && filteredData.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No fee entries found
              </Typography>
            </Box>
          )}
          {filteredData.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: theme.palette.grey[50] }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Standard</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Annual Fees</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Paid Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payment Mode</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        key={row.feesEntryId}
                        hover
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                              {row.studentName?.charAt(0) || 'S'}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {row.studentName || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {row.studentId || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{row.standard || 'N/A'}</TableCell>
                        <TableCell>{formatCurrency(row.annualFees)}</TableCell>
                        <TableCell>
                          <Typography fontWeight={500} color="success.main">
                            {formatCurrency(row.paidAmount)}
                          </Typography>
                          {row.discountAmount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Discount: {formatCurrency(row.discountAmount)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            {formatDate(row.dueDate)}
                          </Box>
                        </TableCell>
                        <TableCell>{getPaymentModeChip(row.paymentMode)}</TableCell>
                        <TableCell>{getStatusChip(row.status)}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewDetails(row)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" color="primary">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedFees(row);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* View Details Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Fee Details</Typography>
              <IconButton onClick={() => setViewDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent>
            {selectedFees && (
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Student Name
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedFees.studentName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Student ID
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedFees.studentId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Standard
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedFees.standard}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Classroom ID
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedFees.classRoomId}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Fee Breakdown
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Fee Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedFees.fees && selectedFees.fees.length > 0 ? (
                        selectedFees.fees.map((fee, index) => (
                          <TableRow key={index}>
                            <TableCell>{fee.feeType || `Fee ${index + 1}`}</TableCell>
                            <TableCell align="right">{formatCurrency(fee.amount || 0)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} align="center">
                            No fee details available
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight={600}>Total Annual Fees</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {formatCurrency(selectedFees.annualFees)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Discount
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      {formatCurrency(selectedFees.discountAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Vehicle Fees
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(selectedFees.vehicleFees)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Paid Amount
                    </Typography>
                    <Typography variant="body1" fontWeight={600} color="success.main">
                      {formatCurrency(selectedFees.paidAmount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    {getStatusChip(selectedFees.status)}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedFees.paymentDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Mode
                    </Typography>
                    {getPaymentModeChip(selectedFees.paymentMode)}
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button variant="contained" startIcon={<ReceiptIcon />}>
              Print Receipt
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the fee entry for{' '}
              <strong>{selectedFees?.studentName}</strong>? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(selectedFees?.feesEntryId)}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={error ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {error || successMessage}
          </Alert>
        </Snackbar>
      </Box>
  );
};

export default FeesManagementPage;